import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTranslation, Translation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';
import { computeAsMixedRatio, componentMoisture, EngineRow } from "./engine/composition";

/* ── Error Boundary ─────────────────────────────────────────────────────── */

class ReportErrorBoundary extends React.Component<
  { onClose: () => void; children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: "" };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <Translation>
          {(t: any) => (
            <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ background: "#fff", borderRadius: "8px", padding: "24px", maxWidth: "400px", textAlign: "center" }}>
                <div style={{ fontSize: "14px", fontWeight: 700, color: "#dc2626", marginBottom: "8px" }}>{t('report.somethingWentWrong')}</div>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "16px" }}>{this.state.error || t('report.somethingWentWrong')}</div>
                <button onClick={this.props.onClose} style={{ padding: "6px 20px", fontSize: "12px", cursor: "pointer", border: "1px solid #d0d0d0", background: "#f5f5f5" }}>{t('common.close')}</button>
              </div>
            </div>
          )}
        </Translation>
      );
    }
    return this.props.children;
  }
}

/* ── Types ──────────────────────────────────────────────────────────────── */

interface Ingredient {
  id: string;
  common_name: string;
  cost_per_kg: number | null;
  moisture: number | null;
  protein_per_100g: number | null;
  fat: number | null;
  satfat: number | null;
  transfat: number | null;
  cholesterol: number | null;
  carbs: number | null;
  fiber: number | null;
  sugar: number | null;
  sodium: number | null;
  vitamin_d: number | null;
  calcium: number | null;
  iron: number | null;
  potassium: number | null;
  digestibility?: number | null;
  aa_his: number | null;
  aa_ile: number | null;
  aa_leu: number | null;
  aa_lys: number | null;
  aa_mc: number | null;
  aa_pt: number | null;
  aa_thr: number | null;
  aa_trp: number | null;
  aa_val: number | null;
  [key: string]: any;
}

interface FormulaRow {
  _rowId: string;
  component: string;
  ingredient_id: string | null;
  grams: number | null;
  common_name: string;
  [key: string]: any;
}

interface FormulaHeader {
  formula_code: string;
  display_name: string | null;
  piece_weight_g: number | null;
  serving_size_g: number | null;
  dough_ratio_pct: number | null;
  owner_name: string | null;
  // Already supplied by FormulaSheet; this interface used to declare them away,
  // which is how the report ended up blending on the wrong basis.
  ratio_mode?: string | null;
  final_moisture_mode?: string | null;
  final_moisture_whole?: number | null;
  final_moisture_dough?: number | null;
  final_moisture_filling?: number | null;
}

interface Props {
  header: FormulaHeader;
  rows: FormulaRow[];
  ingMap: Map<string, Ingredient>;
  onClose: () => void;
  exportType: "pdf" | "ppt";
}

/* ── Constants ──────────────────────────────────────────────────────────── */

const REFERENCE_PATTERN: Record<string, number> = {
  histidine: 19, isoleucine: 28, leucine: 66, lysine: 58,
  methionine_cysteine: 25, phenylalanine_tyrosine: 63,
  threonine: 34, tryptophan: 11, valine: 35,
};

const AA_MAP: Record<string, string> = {
  aa_his: "histidine", aa_ile: "isoleucine", aa_leu: "leucine",
  aa_lys: "lysine", aa_mc: "methionine_cysteine", aa_pt: "phenylalanine_tyrosine",
  aa_thr: "threonine", aa_trp: "tryptophan", aa_val: "valine",
};

const AA_LABELS: Record<string, string> = {
  histidine: "His", isoleucine: "Ile", leucine: "Leu", lysine: "Lys",
  methionine_cysteine: "M+C", phenylalanine_tyrosine: "P+T",
  threonine: "Thr", tryptophan: "Trp", valine: "Val",
};

const DRV: Record<string, number> = {
  fat: 78, satfat: 20, cholesterol: 300, sodium: 2300,
  carbs: 275, fiber: 28, protein: 50, vitaminD: 20,
  calcium: 1300, iron: 18, potassium: 4700,
};

const COLORS = ["#8b5cf6", "#ec4899", "#f59e0b", "#22c55e", "#3b82f6", "#06b6d4", "#ef4444", "#84cc16", "#f97316", "#6366f1", "#14b8a6", "#e879f9"];

/* ── FDA Rounding ───────────────────────────────────────────────────────── */

const fdaRound = {
  calories: (v: number) => v < 5 ? 0 : v <= 50 ? Math.round(v / 5) * 5 : Math.round(v / 10) * 10,
  fat: (v: number) => v < 0.5 ? 0 : v < 5 ? Math.round(v * 2) / 2 : Math.round(v),
  satfat: (v: number) => v < 0.5 ? 0 : v < 5 ? Math.round(v * 2) / 2 : Math.round(v),
  cholesterol: (v: number): string => v < 2 ? "0" : v <= 5 ? "<5" : String(Math.round(v / 5) * 5),
  sodium: (v: number) => v < 5 ? 0 : v <= 140 ? Math.round(v / 5) * 5 : Math.round(v / 10) * 10,
  carbs: (v: number) => v < 0.5 ? 0 : Math.round(v),
  fiber: (v: number) => v < 0.5 ? 0 : Math.round(v),
  sugar: (v: number) => v < 0.5 ? 0 : Math.round(v),
  protein: (v: number) => v < 0.5 ? 0 : Math.round(v),
  vitaminD: (v: number) => v < 0.1 ? 0 : v < 10 ? Math.round(v * 10) / 10 : Math.round(v),
  calcium: (v: number) => v < 10 ? 0 : v <= 100 ? Math.round(v / 10) * 10 : v <= 1000 ? Math.round(v / 50) * 50 : Math.round(v / 100) * 100,
  iron: (v: number) => v < 0.5 ? 0 : v < 2 ? Math.round(v * 10) / 10 : Math.round(v * 2) / 2,
  potassium: (v: number) => v < 5 ? 0 : v <= 140 ? Math.round(v / 5) * 5 : Math.round(v / 10) * 10,
};

/* ── Compute nutrition from formula rows ────────────────────────────────── */

function computeNutrition(rows: FormulaRow[], ingMap: Map<string, Ingredient>, header: FormulaHeader) {
  // A stored 0 would zero the per-serving scale and print "Infinity" per 100g.
  const storedServing = header.serving_size_g;
  const servingG = storedServing != null && storedServing > 0 ? storedServing : 100;

  // Section totals. Grams still drive the recipe tables, which say what to weigh
  // out -- but they are NOT what the nutrition blends on.
  const sectionGrams: Record<string, number> = {};
  for (const r of rows) {
    sectionGrams[r.component] = (sectionGrams[r.component] ?? 0) + (r.grams ?? 0);
  }
  const totalGrams = Object.values(sectionGrams).reduce((a, b) => a + b, 0);
  if (totalGrams === 0) return null;

  // A row's share of its own component, preferring the stored fraction and
  // falling back to its share of the section grams.
  const shareOfComponent = (r: FormulaRow) => {
    if (r.pct_of_component != null) return r.pct_of_component;
    const sectionTotal = sectionGrams[r.component] || 0;
    return sectionTotal > 0 ? (r.grams ?? 0) / sectionTotal : 0;
  };

  // Components blend on the formula's AS-MIXED ratio, not on whatever split the
  // batch happened to be weighed at. F234 is stored 50/50 by grams but is a
  // 60/40 finished formula (66.4/33.6 as-mixed), and blending on the grams put
  // every nutrient in this report on the wrong basis (BUGS.md #22). Same engine
  // and same ratio the calculator uses -- src/engine/composition.ts.
  const engineRows: EngineRow[] = rows
    .filter((r) => r.ingredient_id && ingMap.get(r.ingredient_id))
    .map((r) => ({
      ...(ingMap.get(r.ingredient_id!) as unknown as Record<string, unknown>),
      component: r.component,
      pct_of_component: shareOfComponent(r),
    })) as EngineRow[];

  const { asMixedDough, asMixedFilling } = computeAsMixedRatio({
    doughRatioPct: header.dough_ratio_pct ?? 100,
    ratioMode: (header.ratio_mode ?? "as_mixed") === "finished" ? "finished" : "as-mixed",
    postProcessEnabled: header.final_moisture_mode != null,
    doughMoisturePct: componentMoisture(engineRows, "DOUGH"),
    fillingMoisturePct: componentMoisture(engineRows, "FILLING"),
    moistureMode: header.final_moisture_mode === "composite" ? "whole" : "components",
    targetDoughMoisture: header.final_moisture_dough,
    targetFillingMoisture: header.final_moisture_filling,
    targetWholeMoisture: header.final_moisture_whole,
  });
  const componentRatioPct: Record<string, number> = {
    DOUGH: asMixedDough,
    FILLING: asMixedFilling,
  };

  // Nutrient accumulators
  const nutrients: Record<string, number> = {
    protein: 0, fat: 0, satfat: 0, transfat: 0, cholesterol: 0,
    carbs: 0, fiber: 0, sugar: 0, sodium: 0,
    vitaminD: 0, calcium: 0, iron: 0, potassium: 0, moisture: 0,
  };
  const aaAccum: Record<string, number> = {};
  Object.keys(REFERENCE_PATTERN).forEach((k) => (aaAccum[k] = 0));

  let weightedDigestibility = 0;
  let totalProteinContrib = 0;

  // Ingredient-level data for charts
  const ingredientContribs: { name: string; proteinG: number; color: string; aaContribs: Record<string, number> }[] = [];

  let colorIdx = 0;
  for (const r of rows) {
    if (!r.ingredient_id) continue;
    const ing = ingMap.get(r.ingredient_id);
    if (!ing) continue;
    // Share of the finished blend: the row's share of its own component, times
    // that component's as-mixed ratio. Not its share of the batch grams.
    const fraction = shareOfComponent(r) * ((componentRatioPct[r.component] ?? 0) / 100);

    // Accumulate nutrients per 100g of recipe
    const n = (val: number | null) => (val ?? 0) * fraction;
    nutrients.protein += n(ing.protein_per_100g);
    nutrients.fat += n(ing.fat);
    nutrients.satfat += n(ing.satfat);
    nutrients.transfat += n(ing.transfat);
    nutrients.cholesterol += n(ing.cholesterol);
    nutrients.carbs += n(ing.carbs);
    nutrients.fiber += n(ing.fiber);
    nutrients.sugar += n(ing.sugar);
    nutrients.sodium += n(ing.sodium);
    nutrients.vitaminD += n(ing.vitamin_d);
    nutrients.calcium += n(ing.calcium);
    nutrients.iron += n(ing.iron);
    nutrients.potassium += n(ing.potassium);
    nutrients.moisture += n(ing.moisture);

    // Protein contribution for digestibility weighting
    const proteinContrib = (ing.protein_per_100g ?? 0) * fraction;
    if (proteinContrib > 0) {
      weightedDigestibility += proteinContrib * (ing.digestibility ?? 0.85);
      totalProteinContrib += proteinContrib;
    }

    // AA contributions. The aa_* columns are mg per g of PROTEIN, so they blend
    // by each ingredient's share of the protein, not its share of the mass --
    // the same weighting App.tsx uses (:2107). Accumulated unnormalised here and
    // divided by the total protein contribution after the loop.
    const ingAA: Record<string, number> = {};
    for (const [field, aaKey] of Object.entries(AA_MAP)) {
      const val = (ing as any)[field] ?? 0;
      const contrib = val * proteinContrib;
      aaAccum[aaKey] += contrib;
      ingAA[aaKey] = contrib;
    }

    // Ensure unique names for recharts dataKey (duplicates break stacked bars)
    let ingName = ing.common_name || r.ingredient_id || `Ingredient ${colorIdx + 1}`;
    const existingNames = new Set(ingredientContribs.map((ic) => ic.name));
    if (existingNames.has(ingName)) {
      // Same ingredient in different rows (e.g. dough + filling) — merge AA contributions
      const existing = ingredientContribs.find((ic) => ic.name === ingName);
      if (existing) {
        existing.proteinG += proteinContrib;
        for (const [k, v] of Object.entries(ingAA)) {
          existing.aaContribs[k] = (existing.aaContribs[k] ?? 0) + v;
        }
        continue;
      }
    }
    ingredientContribs.push({
      name: ingName,
      proteinG: proteinContrib,
      color: COLORS[colorIdx % COLORS.length],
      aaContribs: ingAA,
    });
    colorIdx++;
  }

  // Normalise the protein-weighted AA sums back to mg per g of protein.
  if (totalProteinContrib > 0) {
    for (const k of Object.keys(aaAccum)) aaAccum[k] /= totalProteinContrib;
    for (const ic of ingredientContribs) {
      for (const k of Object.keys(ic.aaContribs)) ic.aaContribs[k] /= totalProteinContrib;
    }
  }

  // Digestibility
  const digestibility = totalProteinContrib > 0 ? weightedDigestibility / totalProteinContrib : 0.85;

  // Per serving
  const scale = servingG / 100;
  const perServing: Record<string, number> = {};
  for (const [k, v] of Object.entries(nutrients)) {
    perServing[k] = v * scale;
  }
  const caloriesPerServing = perServing.protein * 4 + perServing.fat * 9 + perServing.carbs * 4;

  // AA scores
  const aaScores: { name: string; total: number; ref: number; score: number; isLimiting: boolean }[] = [];
  let limitingScore = Infinity;
  let limitingAA = "";
  for (const [aa, ref] of Object.entries(REFERENCE_PATTERN)) {
    const total = aaAccum[aa] ?? 0;
    // Both sides are mg per g of protein: aaAccum is now protein-weighted, and
    // REFERENCE_PATTERN is the FAO/WHO pattern in the same units.
    const score = ref > 0 ? total / ref : 0;
    if (score < limitingScore) { limitingScore = score; limitingAA = aa; }
    aaScores.push({ name: AA_LABELS[aa], total, ref, score, isLimiting: false });
  }
  aaScores.forEach((s) => { if (s.name === AA_LABELS[limitingAA]) s.isLimiting = true; });
  const allAbove100 = limitingScore >= 1;
  const pdcaas = Math.min(limitingScore, 1) * digestibility;
  const correctedProtein = perServing.protein * Math.min(pdcaas, 1);
  const percentDV = (correctedProtein / DRV.protein) * 100;

  // Chart data for AA stacked bars (keep only name + ingredient dataKeys for Recharts v3 compatibility)
  const chartData = Object.entries(REFERENCE_PATTERN).map(([aa, ref]) => {
    const entry: any = { name: AA_LABELS[aa] };
    for (const ic of ingredientContribs) {
      entry[ic.name] = ic.aaContribs[aa] ?? 0;
    }
    return entry;
  });

  // Pie chart data
  const macroData = [
    { name: "Water", value: nutrients.moisture, color: "#60a5fa" },
    { name: "Protein", value: nutrients.protein, color: "#22c55e" },
    { name: "Fat", value: nutrients.fat, color: "#fcd34d" },
    { name: "Carbs", value: nutrients.carbs, color: "#f59e0b" },
  ].filter((d) => d.value > 0);

  return {
    nutrients, perServing, caloriesPerServing, scale, servingG, totalGrams,
    sectionGrams, digestibility, pdcaas, correctedProtein, percentDV,
    aaScores, chartData, ingredientContribs, macroData,
    limitingAA, limitingScore, allAbove100,
  };
}

/* ── Sig figs helper ────────────────────────────────────────────────────── */

function sf2(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return "—";
  if (v === 0) return "0";
  const d = Math.floor(Math.log10(Math.abs(v))) + 1;
  const places = Math.max(0, 2 - d);
  return v.toFixed(places);
}

/* ── Safe Tooltip for Recharts v3 ───────────────────────────────────────── */

function SafeTooltip({ active, payload, label }: any) {
  if (!active || !payload) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #ccc", padding: "6px 10px", fontSize: "10px", borderRadius: "4px" }}>
      <div style={{ fontWeight: 700, marginBottom: "2px" }}>{String(label ?? "")}</div>
      {Array.isArray(payload) && payload.map((entry: any, i: number) => (
        <div key={i} style={{ color: entry?.color ?? "#333" }}>
          {String(entry?.name ?? "")}:{" "}
          {typeof entry?.value === "number" ? entry.value.toFixed(1) : String(entry?.value ?? "")}
        </div>
      ))}
    </div>
  );
}

/* ── Chart error boundary ──────────────────────────────────────────────── */

class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode; fallbackLabel?: string },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "20px", textAlign: "center", color: "#999", fontSize: "11px" }}>
          {this.props.fallbackLabel || "Chart unavailable"}
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── Component ──────────────────────────────────────────────────────────── */

export default function FormulaReport(props: Props) {
  return (
    <ReportErrorBoundary onClose={props.onClose}>
      <FormulaReportInner {...props} />
    </ReportErrorBoundary>
  );
}

function FormulaReportInner({ header, rows, ingMap, onClose, exportType }: Props) {
  const { t, i18n } = useTranslation();
  const reportRef = useRef<HTMLDivElement>(null);
  const [generating, setGenerating] = useState(false);
  const [ready, setReady] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const calc = useMemo(() => {
    try {
      return computeNutrition(rows, ingMap, header);
    } catch (e) {
      console.error("Report computeNutrition error:", e);
      return null;
    }
  }, [rows, ingMap, header]);

  const doughRows = useMemo(() => rows.filter((r) => r.component === "DOUGH"), [rows]);
  const fillingRows = useMemo(() => rows.filter((r) => r.component === "FILLING"), [rows]);

  // Translated macro names for pie chart
  const MACRO_NAME_MAP: Record<string, string> = useMemo(() => ({
    Water: t('report.water'),
    Protein: t('report.protein'),
    Fat: t('report.fat'),
    Carbs: t('report.carbs'),
  }), [t]);

  const translatedMacroData = useMemo(() =>
    calc?.macroData.map((d) => ({ ...d, name: MACRO_NAME_MAP[d.name] ?? d.name })) ?? [],
    [calc?.macroData, MACRO_NAME_MAP]
  );

  // Allow charts to render before enabling export
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 800);
    return () => clearTimeout(t);
  }, []);

  // Fetch AI executive summary on mount
  useEffect(() => {
    if (!calc) return;
    let cancelled = false;
    (async () => {
      setAiLoading(true);
      setAiError(null);
      try {
        const doughIngStr = doughRows
          .filter((r) => r.common_name || r.ingredient_id)
          .map((r) => `${r.common_name || r.ingredient_id} (${sf2(r.grams)}g)`)
          .join(", ");
        const fillingIngStr = fillingRows
          .filter((r) => r.common_name || r.ingredient_id)
          .map((r) => `${r.common_name || r.ingredient_id} (${sf2(r.grams)}g)`)
          .join(", ");

        const res = await fetch("/api/report-analysis", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            formulaData: {
              formulaCode: header.formula_code,
              displayName: header.display_name,
              servingG: calc.servingG,
              doughRatioPct: header.dough_ratio_pct ?? 100,
              nutrients: calc.nutrients,
              perServing: calc.perServing,
              caloriesPerServing: calc.caloriesPerServing,
              pdcaas: calc.pdcaas,
              digestibility: calc.digestibility,
              correctedProtein: calc.correctedProtein,
              limitingAA: calc.limitingAA,
              limitingScore: calc.limitingScore,
              allAbove100: calc.allAbove100,
              doughIngredients: doughIngStr,
              fillingIngredients: fillingIngStr,
            },
            lang: i18n.language,
          }),
        });
        if (!cancelled) {
          if (res.ok) {
            const data = await res.json();
            // Guard: ensure analysis is always a string (React #284 fix)
            const summary = data?.analysis;
            setAiSummary(typeof summary === "string" ? summary : summary ? String(summary) : t('report.summaryUnavailable'));
          } else {
            setAiError(t('report.couldNotGenerate'));
          }
        }
      } catch {
        if (!cancelled) setAiError(t('report.couldNotConnect'));
      } finally {
        if (!cancelled) setAiLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [calc, header, doughRows, fillingRows, i18n.language]);

  /* ── Export handlers ── */

  const capturePages = useCallback(async (): Promise<HTMLCanvasElement[]> => {
    const container = reportRef.current;
    if (!container) return [];
    const pages = Array.from(container.querySelectorAll<HTMLDivElement>("[data-report-page]"));
    const canvases: HTMLCanvasElement[] = [];
    for (const page of pages) {
      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      canvases.push(canvas);
    }
    return canvases;
  }, []);

  const exportPdf = useCallback(async () => {
    setGenerating(true);
    try {
      const canvases = await capturePages();
      if (canvases.length === 0) { setGenerating(false); return; }
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;

      for (let i = 0; i < canvases.length; i++) {
        if (i > 0) pdf.addPage();
        const canvas = canvases[i];
        const imgData = canvas.toDataURL("image/png");
        const aspect = canvas.width / canvas.height;
        let w = pageW - margin * 2;
        let h = w / aspect;
        if (h > pageH - margin * 2) {
          h = pageH - margin * 2;
          w = h * aspect;
        }
        pdf.addImage(imgData, "PNG", margin, margin, w, h);
      }
      pdf.save(`${header.formula_code || "report"}_report.pdf`);
    } catch (err) {
      console.error("PDF export error:", err);
      alert(t('report.pdfExportFailed'));
    } finally {
      setGenerating(false);
    }
  }, [capturePages, header.formula_code]);

  const exportPpt = useCallback(async () => {
    setGenerating(true);
    try {
      const canvases = await capturePages();
      if (canvases.length === 0) { setGenerating(false); return; }
      const PptxGenJS = (await import("pptxgenjs")).default;
      const pptx = new PptxGenJS();
      pptx.defineLayout({ name: "LETTER", width: 10, height: 7.5 });
      pptx.layout = "LETTER";

      for (const canvas of canvases) {
        const slide = pptx.addSlide();
        const imgData = canvas.toDataURL("image/png");
        const aspect = canvas.width / canvas.height;
        let w = 9.5;
        let h = w / aspect;
        if (h > 7) { h = 7; w = h * aspect; }
        const x = (10 - w) / 2;
        const y = (7.5 - h) / 2;
        slide.addImage({ data: imgData, x, y, w, h });
      }
      await pptx.writeFile({ fileName: `${header.formula_code || "report"}_report.pptx` });
    } catch (err) {
      console.error("PPT export error:", err);
      alert(t('report.pptExportFailed'));
    } finally {
      setGenerating(false);
    }
  }, [capturePages, header.formula_code]);

  const handleExport = exportType === "pdf" ? exportPdf : exportPpt;

  if (!calc) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <p style={{ textAlign: "center", color: "#666" }}>{t('report.noFormulaData')}</p>
          <button onClick={onClose} style={closeBtnStyle}>{t('common.close')}</button>
        </div>
      </div>
    );
  }

  /* ── Render formulation table ── */
  const renderFormulationTable = (sectionRows: FormulaRow[], sectionName: string) => {
    const sectionTotal = sectionRows.reduce((s, r) => s + (r.grams ?? 0), 0);
    return (
      <div style={{ marginBottom: "12px" }}>
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#1e1b4b", padding: "4px 0", borderBottom: "2px solid #1e1b4b" }}>
          {sectionName}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              <th style={thStyle}>{t('formulation.ingredientHeader')}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>{t('report.grams')}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>{t('report.pctWW')}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>{t('report.proteinCol')}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>{t('report.fatCol')}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>{t('report.carbsCol')}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>{t('report.fiberCol')}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>{t('report.sodiumCol')}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>{t('report.moistureCol')}</th>
            </tr>
          </thead>
          <tbody>
            {sectionRows.map((r) => {
              const ing = r.ingredient_id ? ingMap.get(r.ingredient_id) : null;
              const g = r.grams ?? 0;
              const pct = sectionTotal > 0 ? (g / sectionTotal) * 100 : 0;
              return (
                <tr key={r._rowId} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={tdStyle}>{String(r.common_name || r.ingredient_id || "—")}</td>
                  <td style={tdRightStyle}>{sf2(g)}</td>
                  <td style={tdRightStyle}>{sf2(pct)}%</td>
                  <td style={tdRightStyle}>{sf2(ing?.protein_per_100g)}</td>
                  <td style={tdRightStyle}>{sf2(ing?.fat)}</td>
                  <td style={tdRightStyle}>{sf2(ing?.carbs)}</td>
                  <td style={tdRightStyle}>{sf2(ing?.fiber)}</td>
                  <td style={tdRightStyle}>{sf2(ing?.sodium)}</td>
                  <td style={tdRightStyle}>{sf2(ing?.moisture)}</td>
                </tr>
              );
            })}
            <tr style={{ fontWeight: 700, background: "#f1f5f9", borderTop: "2px solid #1e1b4b" }}>
              <td style={tdStyle}>{t('common.total')}</td>
              <td style={tdRightStyle}>{sf2(sectionTotal)}</td>
              <td style={tdRightStyle}>100%</td>
              <td colSpan={6} style={tdRightStyle}></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: "880px", maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
        {/* Toolbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #e0e0e0", marginBottom: "12px" }}>
          <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e1b4b" }}>
            {t('report.reportPreview')} — {String(header.formula_code ?? "")} {header.display_name ? `(${String(header.display_name)})` : ""}
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <LanguageToggle style={{ padding: "4px 8px", fontSize: "8px", backgroundColor: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.3)", color: "#6d28d9" }} />
            <button onClick={onClose} style={{ padding: "5px 14px", fontSize: "12px", cursor: "pointer", border: "1px solid #d0d0d0", background: "#f5f5f5" }}>{t('common.cancel')}</button>
            <button
              onClick={handleExport}
              disabled={generating || !ready}
              style={{
                padding: "5px 14px", fontSize: "12px", fontWeight: 600, cursor: generating || !ready ? "not-allowed" : "pointer",
                border: "1px solid #7c3aed", background: "#7c3aed", color: "#fff",
                opacity: generating || !ready ? 0.6 : 1,
              }}
            >
              {generating ? t('report.generating') : !ready ? t('report.loading') : exportType === "pdf" ? t('report.downloadPDF') : t('report.downloadPPT')}
            </button>
          </div>
        </div>

        {/* Preview scrollable area */}
        <div style={{ overflowY: "auto", maxHeight: "calc(90vh - 80px)" }} ref={reportRef}>

          {/* ═══ PAGE 1: Header + Formulation Tables ═══ */}
          <div data-report-page="1" style={pageStyle}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", borderBottom: "3px solid #7c3aed", paddingBottom: "8px" }}>
              <div>
                <div style={{ fontSize: "20px", fontWeight: 900, color: "#1e1b4b", fontFamily: "monospace" }}>{String(header.formula_code ?? "")}</div>
                <div style={{ fontSize: "14px", color: "#4b5563" }}>{String(header.display_name || t('report.untitled'))}</div>
              </div>
              <div style={{ textAlign: "right", fontSize: "10px", color: "#6b7280" }}>
                <div>{t('report.servingLabel')} {header.serving_size_g ?? 100}g</div>
                <div>{t('report.doughLabel')} {header.dough_ratio_pct ?? 100}% | {t('report.fillingLabel')} {100 - (header.dough_ratio_pct ?? 100)}%</div>
                <div>{t('report.ownerLabel')} {String(header.owner_name || "—")}</div>
                <div>{t('report.generatedLabel')} {new Date().toLocaleDateString("en-GB")}</div>
              </div>
            </div>

            {/* Formulation tables */}
            {doughRows.length > 0 && renderFormulationTable(doughRows, t('formulation.dough'))}
            {fillingRows.length > 0 && renderFormulationTable(fillingRows, t('formulation.filling'))}

            {/* AI Executive Summary */}
            <div style={{ marginTop: "16px", padding: "12px", background: "#f8f7ff", border: "1px solid #e0dff5", borderRadius: "6px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#5b21b6", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a5 5 0 0 1 5 5v1a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-1l-2 5h-4l-2-5H7a2 2 0 0 1-2-2v-1a2 2 0 0 1 2-2V7a5 5 0 0 1 5-5z" />
                </svg>
                {t('report.executiveSummary')}
              </div>
              {aiLoading && <div style={{ fontSize: "10px", color: "#7c3aed", fontStyle: "italic" }}>{t('report.generatingAnalysis')}</div>}
              {aiError && <div style={{ fontSize: "10px", color: "#999", fontStyle: "italic" }}>{aiError}</div>}
              {aiSummary && (
                <div style={{ fontSize: "10px", color: "#374151", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                  {aiSummary}
                </div>
              )}
              {!aiLoading && !aiError && !aiSummary && (
                <div style={{ fontSize: "10px", color: "#999", fontStyle: "italic" }}>{t('report.summaryUnavailable')}</div>
              )}
            </div>
          </div>

          {/* ═══ PAGE 2: Nutritional Summary + NFP ═══ */}
          <div data-report-page="2" style={pageStyle}>
            <div style={{ display: "flex", gap: "20px" }}>
              {/* Left: Unrounded nutritional data */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "12px", fontWeight: 700, color: "#1e1b4b", marginBottom: "8px", borderBottom: "2px solid #1e1b4b", paddingBottom: "4px" }}>
                  {t('report.nutritionalData')}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={thStyle}>{t('report.nutrient')}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t('report.per100gCol')}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t('report.perServingCol', { g: calc.servingG })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: t('report.caloriesKcal'), per100: calc.caloriesPerServing / calc.scale, perS: calc.caloriesPerServing },
                      { label: t('report.proteinG'), per100: calc.nutrients.protein, perS: calc.perServing.protein },
                      { label: t('report.totalFatG'), per100: calc.nutrients.fat, perS: calc.perServing.fat },
                      { label: t('report.saturatedFatG'), per100: calc.nutrients.satfat, perS: calc.perServing.satfat },
                      { label: t('report.transFatG'), per100: calc.nutrients.transfat, perS: calc.perServing.transfat },
                      { label: t('report.cholesterolMg'), per100: calc.nutrients.cholesterol, perS: calc.perServing.cholesterol },
                      { label: t('report.sodiumMg'), per100: calc.nutrients.sodium, perS: calc.perServing.sodium },
                      { label: t('report.totalCarbsG'), per100: calc.nutrients.carbs, perS: calc.perServing.carbs },
                      { label: t('report.dietaryFiberG'), per100: calc.nutrients.fiber, perS: calc.perServing.fiber },
                      { label: t('report.totalSugarsG'), per100: calc.nutrients.sugar, perS: calc.perServing.sugar },
                      { label: t('report.vitaminDMcg'), per100: calc.nutrients.vitaminD, perS: calc.perServing.vitaminD },
                      { label: t('report.calciumMg'), per100: calc.nutrients.calcium, perS: calc.perServing.calcium },
                      { label: t('report.ironMg'), per100: calc.nutrients.iron, perS: calc.perServing.iron },
                      { label: t('report.potassiumMg'), per100: calc.nutrients.potassium, perS: calc.perServing.potassium },
                      { label: t('report.moisturePct'), per100: calc.nutrients.moisture, perS: calc.perServing.moisture },
                    ].map((row) => (
                      <tr key={row.label} style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={tdStyle}>{row.label}</td>
                        <td style={tdRightStyle}>{sf2(row.per100)}</td>
                        <td style={tdRightStyle}>{sf2(row.perS)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Protein quality card */}
                <div style={{ marginTop: "12px", padding: "10px", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "6px" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#1e1b4b", marginBottom: "6px" }}>{t('report.proteinQuality')}</div>
                  <div style={{ display: "flex", gap: "16px", fontSize: "10px" }}>
                    <div>
                      <div style={{ color: "#6b7280" }}>{t('report.digestibility')}</div>
                      <div style={{ fontSize: "16px", fontWeight: 700, color: "#7c3aed" }}>{((calc.digestibility ?? 0) * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div style={{ color: "#6b7280" }}>{t('report.pdcaas')}</div>
                      <div style={{ fontSize: "16px", fontWeight: 700, color: "#059669" }}>{(Math.min(calc.pdcaas ?? 0, 1) * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div style={{ color: "#6b7280" }}>{t('report.correctedProtein')}</div>
                      <div style={{ fontSize: "16px", fontWeight: 700, color: "#ec4899" }}>{sf2(calc.correctedProtein)}g/srv</div>
                    </div>
                    <div>
                      <div style={{ color: "#6b7280" }}>{t('report.dvProtein')}</div>
                      <div style={{ fontSize: "16px", fontWeight: 700, color: "#3b82f6" }}>{Math.round(calc.percentDV ?? 0)}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: FDA NFP */}
              <div style={{ width: "260px", flexShrink: 0 }}>
                <div style={nfPanelStyle}>
                  <p style={{ fontSize: "28px", fontWeight: 900, margin: 0, lineHeight: 1 }}>Nutrition Facts</p>
                  <div style={nfLine} />
                  <p style={{ fontSize: "11px", fontWeight: 700, margin: "2px 0 0" }}>1 serving per container</p>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: 700, margin: "0 0 1px" }}>
                    <span>Serving size</span>
                    <span>{calc.servingG}g</span>
                  </div>
                  <div style={nfThick} />
                  <div style={{ fontSize: "8px", fontWeight: 900 }}>Amount per serving</div>
                  <div style={{ ...nfRowStyle, alignItems: "baseline" }}>
                    <span style={{ fontSize: "20px", fontWeight: 900 }}>Calories</span>
                    <span style={{ fontSize: "26px", fontWeight: 900 }}>{fdaRound.calories(calc.caloriesPerServing ?? 0)}</span>
                  </div>
                  <div style={nfMedium} />
                  <div style={{ fontSize: "8px", textAlign: "right" as const, fontWeight: 900 }}>% Daily Value*</div>
                  <div style={nfLine} />
                  {/* Total Fat */}
                  <div style={nfRowStyle}>
                    <span><strong>Total Fat</strong> {fdaRound.fat(calc.perServing.fat ?? 0)}g</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.fat ?? 0) / DRV.fat) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Saturated Fat */}
                  <div style={{ ...nfRowStyle, paddingLeft: "16px" }}>
                    <span>Saturated Fat {fdaRound.satfat(calc.perServing.satfat ?? 0)}g</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.satfat ?? 0) / DRV.satfat) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Trans Fat */}
                  <div style={{ ...nfRowStyle, paddingLeft: "16px" }}>
                    <span><em>Trans</em> Fat {fdaRound.fat(calc.perServing.transfat ?? 0)}g</span>
                  </div>
                  <div style={nfLine} />
                  {/* Cholesterol */}
                  <div style={nfRowStyle}>
                    <span><strong>Cholesterol</strong> {fdaRound.cholesterol(calc.perServing.cholesterol ?? 0)}mg</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.cholesterol ?? 0) / DRV.cholesterol) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Sodium */}
                  <div style={nfRowStyle}>
                    <span><strong>Sodium</strong> {fdaRound.sodium(calc.perServing.sodium ?? 0)}mg</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.sodium ?? 0) / DRV.sodium) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Total Carbohydrate */}
                  <div style={nfRowStyle}>
                    <span><strong>Total Carbohydrate</strong> {fdaRound.carbs(calc.perServing.carbs ?? 0)}g</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.carbs ?? 0) / DRV.carbs) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Dietary Fiber */}
                  <div style={{ ...nfRowStyle, paddingLeft: "16px" }}>
                    <span>Dietary Fiber {fdaRound.fiber(calc.perServing.fiber ?? 0)}g</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.fiber ?? 0) / DRV.fiber) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Total Sugars */}
                  <div style={{ ...nfRowStyle, paddingLeft: "16px" }}>
                    <span>Total Sugars {fdaRound.sugar(calc.perServing.sugar ?? 0)}g</span>
                  </div>
                  {/* Added Sugars hairline starts at indent level */}
                  <div style={{ borderTop: "0.5px solid black", margin: "1px 0", marginLeft: "32px" }} />
                  {/* Added Sugars (= Total Sugars) */}
                  <div style={{ ...nfRowStyle, paddingLeft: "32px" }}>
                    <span>Includes {fdaRound.sugar(calc.perServing.sugar ?? 0)}g Added Sugars</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.sugar ?? 0) / 50) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Protein */}
                  {(calc.pdcaas ?? 1) < 0.2 && (
                    <div style={{ fontSize: "10px", fontStyle: "italic" }}>
                      Not a significant source of protein
                    </div>
                  )}
                  <div style={nfRowStyle}>
                    <span><strong>Protein</strong> {fdaRound.protein(calc.perServing.protein ?? 0)}g</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(calc.percentDV ?? 0)}%</span>
                  </div>
                  {/* 7pt heavy bar before vitamins */}
                  <div style={nfThick} />
                  {/* Vitamin D */}
                  <div style={nfRowStyle}>
                    <span>Vitamin D {fdaRound.vitaminD(calc.perServing.vitaminD ?? 0)}mcg</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.vitaminD ?? 0) / DRV.vitaminD) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Calcium */}
                  <div style={nfRowStyle}>
                    <span>Calcium {fdaRound.calcium(calc.perServing.calcium ?? 0)}mg</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.calcium ?? 0) / DRV.calcium) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Iron */}
                  <div style={nfRowStyle}>
                    <span>Iron {fdaRound.iron(calc.perServing.iron ?? 0)}mg</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.iron ?? 0) / DRV.iron) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  {/* Potassium */}
                  <div style={nfRowStyle}>
                    <span>Potassium {fdaRound.potassium(calc.perServing.potassium ?? 0)}mg</span>
                    <span style={{ fontWeight: 900 }}>{Math.round(((calc.perServing.potassium ?? 0) / DRV.potassium) * 100)}%</span>
                  </div>
                  <div style={nfLine} />
                  <p style={{ fontSize: "7px", margin: "4px 0", lineHeight: 1.3 }}>
                    * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ PAGE 3: Charts ═══ */}
          <div data-report-page="3" style={pageStyle}>
            <div style={{ display: "flex", gap: "20px" }}>
              {/* Macro Pie */}
              <div style={{ width: "220px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#1e1b4b", marginBottom: "6px" }}>{t('report.macroComposition')}</div>
                <div style={{ height: "180px" }}>
                  <ChartErrorBoundary fallbackLabel={t('report.pieChartUnavailable')}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={translatedMacroData} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={70} label={({ name, value }: any) => `${String(name ?? "")} ${sf2(value)}g`} labelLine={false}>
                          {translatedMacroData.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartErrorBoundary>
                </div>
              </div>

              {/* AA Score table */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#1e1b4b", marginBottom: "6px" }}>{t('report.aminoAcidScoresVsWHO')}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={thStyle}>{t('report.aminoAcid')}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t('report.productMgG')}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t('report.whoRefMgG')}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t('report.scorePct')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calc.aaScores.map((aa) => (
                      <tr key={aa.name} style={{
                        borderBottom: "1px solid #e5e7eb",
                        background: aa.isLimiting ? "#fef2f2" : "transparent",
                        fontWeight: aa.isLimiting ? 700 : 400,
                      }}>
                        <td style={tdStyle}>{aa.name}{aa.isLimiting ? ` ${t('report.limiting')}` : ""}</td>
                        <td style={tdRightStyle}>{sf2(aa.total)}</td>
                        <td style={tdRightStyle}>{aa.ref}</td>
                        <td style={{ ...tdRightStyle, color: aa.score >= 1 ? "#059669" : "#dc2626" }}>{(aa.score * 100).toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Full AA bar chart */}
            <div style={{ marginTop: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#1e1b4b", marginBottom: "6px" }}>
                {t('report.essentialAAProfile')}
                <span style={{
                  marginLeft: "8px", padding: "2px 10px", borderRadius: "10px", fontSize: "9px", fontWeight: 600,
                  background: calc.allAbove100 ? "#dbeafe" : "#fef2f2",
                  color: calc.allAbove100 ? "#1d4ed8" : "#dc2626",
                }}>
                  {calc.allAbove100 ? t('report.allAaMet') : t('formulation.limitingAA', { aa: `${AA_LABELS[calc.limitingAA] ?? calc.limitingAA} (${((calc.limitingScore ?? 0) * 100).toFixed(0)}%)` })}
                </span>
              </div>
              <div style={{ height: "220px" }}>
                <ChartErrorBoundary fallbackLabel={t('report.barChartUnavailable')}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={calc.chartData} margin={{ top: 10, right: 20, left: 15, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#6b7280" }} />
                      <YAxis tick={{ fontSize: 8, fill: "#6b7280" }} label={{ value: t('report.mgPerGProtein'), angle: -90, position: "insideLeft", fontSize: 8, fill: "#6b7280" }} />
                      <Tooltip content={<SafeTooltip />} />
                      {calc.ingredientContribs.map((ic) => (
                        <Bar key={ic.name} dataKey={ic.name} stackId="a" fill={ic.color} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartErrorBoundary>
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center", marginTop: "4px" }}>
                {calc.ingredientContribs.map((ic) => (
                  <span key={ic.name} style={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "8px", color: "#4b5563" }}>
                    <span style={{ width: "8px", height: "8px", background: ic.color, borderRadius: "2px", display: "inline-block" }} />
                    {ic.name}
                  </span>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────────────────── */

const overlayStyle: React.CSSProperties = {
  position: "fixed", inset: 0, zIndex: 10000,
  background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center",
};

const modalStyle: React.CSSProperties = {
  background: "#fff", borderRadius: "8px", padding: "16px 20px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column",
};

const closeBtnStyle: React.CSSProperties = {
  margin: "12px auto 0", padding: "6px 20px", fontSize: "12px", cursor: "pointer",
  border: "1px solid #d0d0d0", background: "#f5f5f5",
};

const pageStyle: React.CSSProperties = {
  background: "#fff", padding: "24px", marginBottom: "16px",
  border: "1px solid #e0e0e0", borderRadius: "4px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  minHeight: "400px",
};

const thStyle: React.CSSProperties = {
  padding: "4px 6px", textAlign: "left", fontWeight: 700, fontSize: "9px",
  textTransform: "uppercase", letterSpacing: "0.5px", color: "#374151",
  borderBottom: "2px solid #d1d5db",
};

const tdStyle: React.CSSProperties = { padding: "3px 6px" };
const tdRightStyle: React.CSSProperties = { padding: "3px 6px", textAlign: "right", fontFamily: "monospace" };

const nfPanelStyle: React.CSSProperties = {
  fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif",
  border: "0.5pt solid black", padding: "3pt", width: "260px", background: "white", color: "black",
};
const nfLine: React.CSSProperties = { borderTop: "0.5px solid black", margin: "1px 0" };
const nfThick: React.CSSProperties = { borderTop: "7pt solid black", margin: "0" };
const nfMedium: React.CSSProperties = { borderTop: "3pt solid black", margin: "0" };
const nfRowStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", fontSize: "11px", padding: "1px 0",
};
